import { fmtDisplayDate } from '@utils/displayDate'

/**
 * The "Filters — …" line for specialized report exports (CC-50029).
 *
 * WHY THIS EXISTS: the date-range/criteria row in an exported CSV, Excel or PDF
 * is emitted from exactly one input — ReportExport's `filtersSummary` prop
 * (`filtersLine`). Only two callers in the whole repo ever passed it:
 * `ReportView` (the generic engine) and `ARReport`. Every page that renders
 * through `SpecializedReportShell` left it undefined, so their exports carried a
 * title and a "Generated …" stamp but never said what period or basis the
 * numbers covered — reported on P&L, and true of Trial Balance, Balance Sheet
 * and General Ledger identically.
 *
 * For a financial statement that is a correctness problem, not a cosmetic one: a
 * balance sheet with no as-of date is indistinguishable from any other run of
 * the same report.
 *
 * These helpers keep the wording identical across pages so one report's export
 * header can be compared with another's. Dates are MM/DD/YYYY to match the
 * UsDateInput the filter bars render, and the separator is " to " rather than an
 * arrow because jsPDF's WinAnsi Helvetica cannot encode "→" (it prints as
 * mojibake — see winAnsiSafe in exportClientPdf).
 */

/**
 * MM/DD/YYYY, matching UsDateInput. Empty string for a missing date.
 *
 * Now an alias of `fmtDisplayDate` (2026-08-27) so Report Center has ONE display
 * formatter rather than a copy per surface. The exported NAME is unchanged on purpose
 * — 19 specialized pages import `fmtSummaryDate`, and this is not a rename. One
 * behavior change: an unparseable date renders as '' instead of "Invalid Date"; see
 * fmtDisplayDate's own note on why a blank beats a half-formatted date on a financial
 * statement's criteria line.
 */
export const fmtSummaryDate = fmtDisplayDate

/**
 * A date window as users describe it. A statement with only an end date is
 * point-in-time ("As of"), a ledger with both is a range.
 */
export const dateRangePart = (startDate, endDate) => {
  if (startDate && endDate) return `${fmtSummaryDate(startDate)} to ${fmtSummaryDate(endDate)}`
  if (endDate) return `As of ${fmtSummaryDate(endDate)}`
  if (startDate) return `From ${fmtSummaryDate(startDate)}`
  return ''
}

/** Cash vs accrual — always stated, because it changes every figure. */
export const basisPart = (isCashBasis) => (isCashBasis ? 'Cash basis' : 'Accrual basis')

/**
 * CC-50326 — the account scope of a General Ledger, always stated.
 *
 * "All account types" is said out loud rather than left blank: the reported
 * defect is that a reader could not tell a partial General Ledger from a
 * complete one, and a silent default is exactly how that happens. `token` is the
 * pipe-joined account-category token; `labels` maps a category value to its name.
 */
export const accountTypesPart = (token, labels) => {
  const names = String(token || '')
    .split('|')
    .filter(Boolean)
    .map((v) => labels?.get?.(v) || v)
  return names.length ? `Account types: ${names.join(', ')}` : 'All account types'
}

/** Join the parts a page considers describable, dropping empties. */
export const describeFilters = (...parts) =>
  parts.filter((p) => p != null && String(p).trim() !== '').join(' · ')
